import { Injectable, inject } from '@angular/core';
import { Observable, firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { PhotographyApiService } from './api.service';
import { PhotographyListResponse } from '../models/photography-vendor.model';
import {
  PhotographyAddZipCodesResult, PhotographyRegion, PhotographyRegionZipCode,
} from '../models/photography-catalog.model';

/** Shared pricing regions in the lcms-photography catalog, used across vendors */
@Injectable({
  providedIn: 'root',
})
export class PhotographyRegionService {
  private api = inject(PhotographyApiService);

  readonly resource = 'regions';

  /** The API caps a page at 200 */
  list$(limit = 200, offset = 0): Observable<PhotographyRegion[]> {
    return this.api.get<PhotographyListResponse<PhotographyRegion>>(this.resource, { limit, offset }).pipe(
      map((response) => response.items),
    );
  }

  get$(regionId: string): Observable<PhotographyRegion> {
    return this.api.get<PhotographyRegion>(`${this.resource}/${regionId}`);
  }

  create(name: string): Promise<PhotographyRegion> {
    return this.api.create<PhotographyRegion, { name: string }>(this.resource, { name });
  }

  update(regionId: string, name: string): Promise<PhotographyRegion> {
    return this.api.update<PhotographyRegion, { name: string }>(`${this.resource}/${regionId}`, { name });
  }

  /** Rejects with a 409 while anything still references the region */
  delete(regionId: string): Promise<void> {
    return firstValueFrom(this.api.delete<void>(`${this.resource}/${regionId}`));
  }

  zipCodes$(regionId: string): Observable<PhotographyRegionZipCode[]> {
    return this.api.get<{ items: PhotographyRegionZipCode[] }>(`${this.resource}/${regionId}/zip-codes`).pipe(
      map((response) => response.items),
    );
  }

  /** Adds 5-digit zips or prefixes like "802*". Entries that match nothing come back in `unresolved`. */
  addZipCodes(regionId: string, zipCodes: string[]): Promise<PhotographyAddZipCodesResult> {
    const route = `${this.resource}/${regionId}/zip-codes`;
    return firstValueFrom(this.api.put<PhotographyAddZipCodesResult>(route, { zipCodes }));
  }

  removeZipCode(regionId: string, zipCodeId: string): Promise<void> {
    return firstValueFrom(this.api.delete<void>(`${this.resource}/${regionId}/zip-codes/${zipCodeId}`));
  }
}
